import React, { useState, useEffect } from "react";
import { Bar, Pie, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
} from 'chart.js';
import "./QDashboard.css";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

function Dashboard() {
  const [stats, setStats] = useState({
    totalQuotations: 0,
    totalEarnings: 0,
    totalBalance: 0, // New field for sum of all balances
    pendingCount: 0,
    completedCount: 0,
    discardedCount: 0,
    monthlyData: [],
    statusDistribution: []
  });
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('monthly');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('http://priyanthabandara.lk/get_dashboard_stats.php');
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        
        const processedData = {
          ...data,
          statusDistribution: [
            { status: 'Completed', count: data.completedCount, color: '#10B981' },
            { status: 'Pending', count: data.pendingCount, color: '#F59E0B' },
            { status: 'Discarded', count: data.discardedCount, color: '#EF4444' }
          ]
        };
        
        setStats(processedData);
        setLoading(false);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        setLoading(false);
        const mockData = {
          totalQuotations: 0,
          totalEarnings: 0,
          totalBalance: 0,
          pendingCount: 0,
          completedCount: 0,
          discardedCount: 0,
          monthlyData: [],
          statusDistribution: [
            { status: 'Completed', count: 0, color: '#10B981' },
            { status: 'Pending', count: 0, color: '#F59E0B' },
            { status: 'Discarded', count: 0, color: '#EF4444' }
          ]
        };
        setStats(mockData);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount).replace('LKR', 'Rs.');
  };

  // Simple financial calculations independent of status
const totalReceived = stats.totalEarnings - stats.totalBalance;
const totalDue = stats.totalBalance;
const discardedAmount = stats.discardedAmount || 0;

  // Chart data filtering
  const getFilteredChartData = () => {
    switch(timeFilter) {
      case 'weekly':
        return {
          earnings: groupDataByWeek(stats.monthlyData, 'earnings'),
          quotations: groupDataByWeek(stats.monthlyData, 'quotations')
        };
      case 'yearly':
        return {
          earnings: groupDataByYear(stats.monthlyData, 'earnings'),
          quotations: groupDataByYear(stats.monthlyData, 'quotations')
        };
      default:
        return {
          earnings: stats.monthlyData.map(item => item.earnings),
          quotations: stats.monthlyData.map(item => item.quotations),
          labels: stats.monthlyData.map(item => item.month)
        };
    }
  };

  const groupDataByWeek = (data, field) => data.map(item => item[field]);
  const groupDataByYear = (data, field) => data.map(item => item[field]);

  const filteredData = getFilteredChartData();

  const earningsChartData = {
    labels: filteredData.labels || stats.monthlyData.map(item => item.month),
    datasets: [{
      label: 'Earnings',
      data: filteredData.earnings,
      backgroundColor: 'rgba(59, 130, 246, 0.5)',
      borderColor: 'rgba(59, 130, 246, 1)',
      borderWidth: 2,
      tension: 0.4
    }]
  };

  const quotationsChartData = {
    labels: filteredData.labels || stats.monthlyData.map(item => item.month),
    datasets: [{
      label: 'Quotations',
      data: filteredData.quotations,
      backgroundColor: 'rgba(16, 185, 129, 0.5)',
      borderColor: 'rgba(16, 185, 129, 1)',
      borderWidth: 2,
      tension: 0.4
    }]
  };

  const statusDistributionData = {
    labels: stats.statusDistribution.map(item => item.status),
    datasets: [{
      data: stats.statusDistribution.map(item => item.count),
      backgroundColor: stats.statusDistribution.map(item => item.color),
      borderWidth: 1
    }]
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Dashboard Overview - Priyantha Bandara Photography</h1>
      </div>

      <div className="stats-grid">
        {/* 1. Total Earnings */}
        <div className="stat-card success">
          <div className="stat-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{formatCurrency(stats.totalEarnings)}</div>
            <div className="stat-label">Total Earnings</div>
            <div className="stat-subvalue">{stats.totalQuotations} quotations</div>
          </div>
        </div>

        {/* 2. Payment Received */}
        <div className="stat-card info">
          <div className="stat-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{formatCurrency(totalReceived)}</div>
            <div className="stat-label">Payment Received</div>
            <div className="stat-subvalue">Collected payments</div>
          </div>
        </div>

        {/* 3. Due Amount */}
        <div className="stat-card warning">
          <div className="stat-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{formatCurrency(totalDue)}</div>
            <div className="stat-label">Due Amount</div>
            <div className="stat-subvalue">Pending collections</div>
          </div>
        </div>

        {/* 4. Completed Quotations */}
        <div className="stat-card primary">
          <div className="stat-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.completedCount}</div>
            <div className="stat-label">Completed</div>
            <div className="stat-subvalue">Quotations</div>
          </div>
        </div>

        {/* 5. Pending Quotations */}
        <div className="stat-card warning">
          <div className="stat-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.pendingCount}</div>
            <div className="stat-label">Pending</div>
            <div className="stat-subvalue">Quotations</div>
          </div>
        </div>

        {/* 6. Discarded Quotations */}
        <div className="stat-card danger">
          <div className="stat-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.discardedCount}</div>
            <div className="stat-label">Discarded</div>
            <div className="stat-subvalue">Quotations</div>
          </div>
        </div>

        {/* 7. Completion Rate */}
        <div className="stat-card secondary">
          <div className="stat-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">
              {stats.totalQuotations > 0 ? 
                Math.round((stats.completedCount / stats.totalQuotations) * 100) : 0}%
            </div>
            <div className="stat-label">Completion Rate</div>
            <div className="stat-subvalue">
              {stats.completedCount} of {stats.totalQuotations}
            </div>
          </div>
        </div>
      </div>

      <div className="chart-filters">
        <div className="filter-dropdown">
          <label htmlFor="time-filter">View by: </label>
          <select
            id="time-filter"
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      </div>

      <div className="charts-section">
        <div className="chart-container">
          <h3 className="chart-title">Earnings ({timeFilter})</h3>
          <div className="chart-wrapper">
            <Line 
              data={earningsChartData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: 'top' },
                  tooltip: {
                    callbacks: {
                      label: (context) => formatCurrency(context.raw)
                    }
                  }
                },
                scales: {
                  y: {
                    ticks: {
                      callback: (value) => formatCurrency(value)
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        <div className="chart-container">
          <h3 className="chart-title">Quotations ({timeFilter})</h3>
          <div className="chart-wrapper">
            <Bar 
              data={quotationsChartData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: 'top' }
                }
              }}
            />
          </div>
        </div>

        <div className="chart-container">
          <h3 className="chart-title">Quotation Status Distribution</h3>
          <div className="chart-wrapper">
            <Pie 
              data={statusDistributionData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: 'right' },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = Math.round((value / total) * 100);
                        return `${label}: ${value} (${percentage}%)`;
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;